import React, { useRef, useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Card, CardContent } from "@/modern/components/ui/card";
import { Button } from "@/modern/components/ui/button";
import { Textarea } from "@/modern/components/ui/textarea";
import { ScrollArea } from "@/modern/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/modern/components/ui/avatar";
import { MarkdownRenderer } from "@/modern/components/ui/markdown-renderer";
import { cn } from "@/modern/utils";
import { sendChatMessage, getChatSessionHistory, getChatSessions, deleteChatSession } from "@/network/network";
import type { ChatMessage, SendMessageRequest, SendMessageResponse, SessionHistoryResponse, ChatSession, ListSessionsResponse } from "./types";
import moment from "moment";

interface SwivuSolChatBoxProps {
  className?: string;
  placeholder?: string;
  welcomeMessage?: string;
  boardId?: number;
  boardViewId?: number;
  sessionId?: string;
  onSessionChange?: (sessionId: string) => void;
  currentUser?: any; // User object from BryntumContext
}

export const SwivuSolChatBox: React.FC<SwivuSolChatBoxProps> = ({
  className,
  placeholder = "Ask Swivu Sol anything...",
  welcomeMessage = "Hello! I'm Swivu Sol, your AI assistant. How can I help you today?\n\n⚠️ **Experimental Feature Notice**\n\nThis AI assistant is currently in active development and testing. You may experience:\n- Slower response times than expected\n- Occasional inaccurate or incomplete information\n- Unexpected behavior\n\nWe're working hard to improve the experience. Your feedback helps us make Swivu Sol better!",
  boardId,
  boardViewId,
  sessionId: initialSessionId,
  onSessionChange,
  currentUser,
}) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | undefined>(initialSessionId);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleImageError = () => setImageError(true);

  const userDisplayName = currentUser?.first_name || currentUser?.name || "User";
  const userProfileImageUrl = currentUser?.profile_image_url;

  // Load sessions when component mounts or boardId changes
  useEffect(() => {
    fetchSessions();
  }, [boardId]);

  // Load session history if sessionId is provided
  useEffect(() => {
    if (initialSessionId && messages.length === 0) {
      loadSessionHistory(initialSessionId);
    }
  }, [initialSessionId]);

  const fetchSessions = async () => {
    setIsLoadingSessions(true);
    try {
      const params = boardId ? { board_id: boardId, limit: 50 } : { limit: 50 };
      const response = await getChatSessions(params);
      const data: ListSessionsResponse = response.data;
      setSessions(data.sessions);
    } catch (err: any) {
      console.error("Failed to fetch sessions:", err);
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const loadSessionHistory = async (sessionId: string) => {
    setIsLoadingHistory(true);
    setError(null);
    try {
      const response = await getChatSessionHistory(sessionId);
      const data: SessionHistoryResponse = response.data;

      // Convert API messages to our format
      const formattedMessages: ChatMessage[] = data.messages.map(msg => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        created_at: msg.created_at,
        tool_calls: msg.tool_calls,
      }));

      setMessages(formattedMessages);
      setSessionId(sessionId);
      onSessionChange?.(sessionId);
    } catch (err: any) {
      console.error("Failed to load session history:", err);
      setError("Failed to load conversation history");
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleSelectSession = (session: ChatSession) => {
    loadSessionHistory(session.id);
    setShowSidebar(false); // Close sidebar on mobile
  };

  const handleNewChat = () => {
    setMessages([]);
    setSessionId(undefined);
    setError(null);
    onSessionChange?.("");
    setShowSidebar(false);
  };

  const handleDeleteSession = async (sessionIdToDelete: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent session selection
    try {
      await deleteChatSession(sessionIdToDelete);
      // Refresh sessions list
      await fetchSessions();
      // If deleted session was active, start new chat
      if (sessionIdToDelete === sessionId) {
        handleNewChat();
      }
    } catch (err: any) {
      console.error("Failed to delete session:", err);
      setError("Failed to delete conversation");
    }
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector("[data-radix-scroll-area-viewport]");
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [input]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement> | React.KeyboardEvent) => {
    e.preventDefault();

    if (!input || !input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    setError(null);

    // Add user message to UI immediately
    const tempUserMessage: ChatMessage = {
      id: `temp-user-${Date.now()}`,
      role: "user",
      content: userMessage,
      created_at: new Date().toISOString(),
    };

    setMessages(prev => [...prev, tempUserMessage]);
    setIsLoading(true);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }

    try {
      const requestBody: SendMessageRequest = {
        message: userMessage,
      };

      // Add session context if available
      if (sessionId) {
        requestBody.session_id = sessionId;
      }
      if (boardId) {
        requestBody.board_id = boardId;
      }
      if (boardViewId) {
        requestBody.boardview_id = boardViewId;
      }

      requestBody.local_datetime = moment().format("YYYY-MM-DDTHH:mm:ssZ");

      const response = await sendChatMessage(requestBody);
      const data: SendMessageResponse = response.data;

      // Update session ID if it's a new session
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
        onSessionChange?.(data.session_id);
      }

      // Add assistant response to messages
      const assistantMessage: ChatMessage = {
        id: data.message_id,
        role: "assistant",
        content: data.response,
        created_at: new Date().toISOString(),
        tool_calls: data.tools_used?.length > 0
          ? data.tools_used.map(tool => ({ name: tool, args: {} }))
          : null,
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Refresh sessions list if this was a new session
      if (!sessionId) {
        fetchSessions();
      }

    } catch (err: any) {
      console.error("Failed to send message:", err);
      const errorMessage = err?.response?.data?.detail || "Failed to send message. Please try again.";
      setError(errorMessage);

      // Add error message to UI
      const errorMsg: ChatMessage = {
        id: `error-${Date.now()}`,
        role: "assistant",
        content: `⚠️ Error: ${errorMessage}`,
        created_at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (input && input.trim() && !isLoading) {
        handleSubmit(e);
      }
    }
  };

  return (
    <Card className={cn("flex flex-col h-full overflow-hidden bg-background", className)}>
      <CardContent className="flex h-full p-0 relative">
        {/* Backdrop */}
        <AnimatePresence>
          {showSidebar && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowSidebar(false)}
              className="absolute inset-0 bg-black/20 z-10"
            />
          )}
        </AnimatePresence>

        {/* Animated Sidebar Overlay */}
        <AnimatePresence mode="wait">
          {showSidebar && (
            <motion.div
              initial={{ x: -280, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -280, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="absolute left-0 top-0 bottom-0 w-72 border-r border-border flex flex-col bg-background shadow-xl z-20"
            >
              {/* Sidebar Header */}
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-clock-rotate-left text-base text-muted-foreground" />
                    <h3 className="font-semibold text-base">History</h3>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="size-8 hover:bg-muted"
                    onClick={() => setShowSidebar(false)}
                  >
                    <i className="fa-solid fa-times text-sm" />
                  </Button>
                </div>
                <Button
                  size="sm"
                  onClick={handleNewChat}
                  className="w-full h-10"
                >
                  <i className="fa-solid fa-plus mr-2" />
                  New Conversation
                </Button>
              </div>

              {/* Sessions List */}
              <ScrollArea className="flex-1" forceBlockDisplay>
                {isLoadingSessions ? (
                  <div className="p-4 text-center">
                    <i className="fa-solid fa-spinner fa-spin text-base text-muted-foreground" />
                  </div>
                ) : sessions.length === 0 ? (
                  <div className="p-6 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="size-10 rounded-full bg-muted flex items-center justify-center">
                        <i className="fa-solid fa-inbox text-lg text-muted-foreground" />
                      </div>
                      <p className="text-xs text-muted-foreground">No conversations yet</p>
                    </div>
                  </div>
                ) : (
                  <div className="px-2 py-2 space-y-1">
                    {sessions.map((session) => (
                      <motion.div
                        key={session.id}
                        whileHover={{ scale: 1.005 }}
                        whileTap={{ scale: 0.995 }}
                        className={cn(
                          "rounded-lg transition-all group relative",
                          session.id === sessionId
                            ? "bg-primary/10"
                            : "hover:bg-muted"
                        )}
                      >
                        <button
                          onClick={() => handleSelectSession(session)}
                          className="w-full text-left py-3 pl-3 pr-14 flex items-start"
                        >
                          <div className="flex-1 min-w-0 pr-2">
                            <p className={cn(
                              "text-sm font-medium truncate mb-1",
                              session.id === sessionId ? "text-primary" : ""
                            )}>
                              {session.title || "New conversation"}
                            </p>
                            <span className="text-xs text-muted-foreground">
                              {session.message_count} message{session.message_count !== 1 ? "s" : ""}
                            </span>
                          </div>
                        </button>
                        <button
                          onClick={(e) => handleDeleteSession(session.id, e)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 size-7 rounded-md hover:bg-destructive/10 hover:text-destructive transition-opacity flex items-center justify-center"
                          aria-label="Delete session"
                        >
                          <i className="fa-solid fa-trash text-xs" />
                        </button>
                      </motion.div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Chat Area */}
        <div className="flex flex-col flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-background">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setShowSidebar(!showSidebar)}
              className="size-8 hover:bg-muted"
            >
              <i className={cn(
                "text-sm transition-transform",
                showSidebar ? "fa-solid fa-chevron-left" : "fa-solid fa-bars"
              )} />
            </Button>
            <div className="flex items-center justify-center size-9 rounded-lg bg-primary/10 p-1.5">
              <img src="/assets/swivu-logo.png" alt="Swivu" className="w-full h-full object-contain" />
            </div>
            <div className="flex-1 min-w-0 flex items-baseline gap-2">
              <h3 className="text-base font-semibold leading-none">Swivu Sol</h3>
              <span className="px-1.5 py-0.5 text-[10px] font-medium leading-none bg-amber-500/20 text-amber-700 dark:text-amber-400 rounded border border-amber-500/30">
                EXPERIMENTAL
              </span>
            </div>
          </div>

        {/* Messages Area */}
        <ScrollArea ref={scrollAreaRef} className="flex-1 p-4">
          <div className="space-y-4 max-w-3xl mx-auto">
            {/* Loading history indicator */}
            {isLoadingHistory && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-center py-8"
              >
                <i className="fa-solid fa-spinner fa-spin text-xl text-primary" />
              </motion.div>
            )}

            {/* Welcome message */}
            {!isLoadingHistory && messages.length === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="flex gap-3"
              >
                <div className="flex items-center justify-center size-9 rounded-lg bg-primary/10 flex-shrink-0 p-1.5">
                  <img src="/assets/swivu-logo.png" alt="Swivu" className="w-full h-full object-contain" />
                </div>
                <div className="flex-1">
                  <div className="bg-muted/50 border border-border rounded-xl p-3">
                    <MarkdownRenderer content={welcomeMessage} />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Messages */}
            {messages.map((message, index) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={cn(
                  "flex gap-3",
                  message.role === "user" ? "flex-row-reverse" : ""
                )}
              >
                <div className="flex-shrink-0">
                  {message.role === "user" ? (
                    imageError || !userProfileImageUrl ? (
                      <Avatar className="size-9">
                        <AvatarFallback className="bg-blue-600 text-white font-semibold text-sm">
                          {userDisplayName.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    ) : (
                      <Avatar className="size-9">
                        <AvatarImage
                          src={userProfileImageUrl}
                          alt="User Avatar"
                          onError={handleImageError}
                        />
                        <AvatarFallback className="bg-blue-600 text-white font-semibold text-sm">
                          {userDisplayName.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    )
                  ) : message.role === "tool" ? (
                    <div className="flex items-center justify-center size-9 rounded-lg bg-primary/10">
                      <i className="fa-solid fa-wrench text-sm text-primary" />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center size-9 rounded-lg bg-primary/10 p-1.5">
                      <img src="/assets/swivu-logo.png" alt="Swivu" className="w-full h-full object-contain" />
                    </div>
                  )}
                </div>
                <div className="flex-1 max-w-[80%]">
                  <div
                    className={cn(
                      "rounded-xl",
                      message.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted/50 border border-border"
                    )}
                  >
                    <div className="p-3">
                      {message.role === "user" ? (
                        // User messages: preserve whitespace and newlines
                        <div className="text-sm whitespace-pre-wrap break-words">
                          {message.content}
                        </div>
                      ) : (
                        // Assistant messages: render as markdown
                        <MarkdownRenderer content={message.content} />
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex gap-3"
              >
                <div className="flex items-center justify-center size-9 rounded-lg bg-primary/10 flex-shrink-0 p-1.5">
                  <img src="/assets/swivu-logo.png" alt="Swivu" className="w-full h-full object-contain" />
                </div>
                <div className="flex-1">
                  <div className="bg-muted/50 border border-border rounded-xl p-3">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <motion.div
                          animate={{ y: [0, -6, 0] }}
                          transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                          className="w-1.5 h-1.5 bg-primary/70 rounded-full"
                        />
                        <motion.div
                          animate={{ y: [0, -6, 0] }}
                          transition={{ duration: 0.6, repeat: Infinity, delay: 0.1 }}
                          className="w-1.5 h-1.5 bg-primary/70 rounded-full"
                        />
                        <motion.div
                          animate={{ y: [0, -6, 0] }}
                          transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                          className="w-1.5 h-1.5 bg-primary/70 rounded-full"
                        />
                      </div>
                      <span className="text-sm text-muted-foreground">Thinking...</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div className="p-4 border-t border-border bg-background">
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mb-3 p-2 bg-destructive/10 border border-destructive/30 rounded-lg text-sm text-destructive flex items-start gap-2"
              >
                <i className="fa-solid fa-circle-exclamation mt-0.5" />
                <span>{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleSubmit} className="space-y-2">
            <div className="relative">
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                disabled={isLoading || isLoadingHistory}
                className="pr-12 resize-none min-h-[48px] max-h-[200px] rounded-lg"
                rows={1}
              />
              <Button
                type="submit"
                size="icon"
                disabled={!input || !input.trim() || isLoading || isLoadingHistory}
                className="absolute right-2 bottom-2 size-8 rounded-lg"
              >
                {isLoading ? (
                  <i className="fa-solid fa-spinner fa-spin text-sm" />
                ) : (
                  <i className="fa-solid fa-paper-plane text-sm" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              <kbd className="px-1.5 py-0.5 text-[10px] bg-muted rounded border border-border">Enter</kbd> to send • <kbd className="px-1.5 py-0.5 text-[10px] bg-muted rounded border border-border">Shift + Enter</kbd> for new line
            </p>
          </form>
        </div>
        </div>
      </CardContent>
    </Card>
  );
};
